import {
  useEffect,
  useRef,
  useState,
  type FormEvent,
} from 'react';
import { useNavigate, useSearchParams } from '@/lib/router';
import { supabase } from '../lib/supabase';
import { redirectToCheckout } from '../lib/stripe';
import { Wordmark } from '../components/Wordmark';
import { Turnstile } from '../components/Turnstile';
import { btnBase, btnPrimary, field, fieldInput, fieldLabel } from '@/lib/ui';
import { cn } from '@/lib/cn';
import { PRO_PRICE_LABEL, PRO_TRIAL_SUMMARY } from '@/lib/pricing';
import {
  authFailureMessage,
  buildAuthCallbackUrl,
  safeAuthNext,
  type AuthOperation,
} from '@/lib/auth/recovery';

type Mode = 'signin' | 'signup' | 'forgot' | 'reset';
type Plan = 'free' | 'pro';

const QUERY_ERRORS: Record<string, string> = {
  unauthorized: 'Your session is unavailable. Sign in to continue.',
  confirmation_failed:
    'We couldn’t confirm your account. The link may be expired or already used.',
  recovery_failed:
    'We couldn’t open that password-reset link. Request a new one below.',
};

// Cloudflare Turnstile site key. When unset in local development, the widget
// is omitted and auth remains exercisable without a network challenge.
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as
  | string
  | undefined;
const DEV_TURNSTILE_SITE_KEY = '1x00000000000000000000AA';
const RESEND_COOLDOWN_SECONDS = 45;

export function resolveTurnstileSiteKey(
  configured: string | undefined,
  authState: string | null,
  development = import.meta.env.DEV,
): string | undefined {
  if (configured) return configured;
  return development && authState === 'turnstile'
    ? DEV_TURNSTILE_SITE_KEY
    : undefined;
}

function modeFromQuery(value: string | null): Mode {
  if (value === 'signup' || value === 'forgot' || value === 'reset') return value;
  return 'signin';
}

export function Auth() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = safeAuthNext(searchParams.get('next'));
  const turnstileSiteKey = resolveTurnstileSiteKey(
    TURNSTILE_SITE_KEY,
    searchParams.get('authState'),
  );

  const [mode, setMode] = useState<Mode>(() =>
    modeFromQuery(searchParams.get('mode')),
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [plan, setPlan] = useState<Plan>(
    searchParams.get('plan') === 'pro' ? 'pro' : 'free',
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(
    () => QUERY_ERRORS[searchParams.get('error') ?? ''] ?? null,
  );
  const [confirmSentTo, setConfirmSentTo] = useState<{
    email: string;
    plan: Plan;
  } | null>(null);
  const [resetSentTo, setResetSentTo] = useState<string | null>(null);
  const [resetComplete, setResetComplete] = useState(false);

  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaNonce, setCaptchaNonce] = useState(0);
  const [resendStatus, setResendStatus] = useState<
    'idle' | 'sending' | 'sent' | 'error'
  >('idle');
  const [resendError, setResendError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);

  const submitLockRef = useRef(false);
  const submitAttemptRef = useRef(0);
  const resendLockRef = useRef(false);
  const resendAttemptRef = useRef(0);
  const errorRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const previousModeRef = useRef(mode);

  const captchaRequired = Boolean(turnstileSiteKey);
  const captchaMissing =
    mode !== 'reset' && captchaRequired && !captchaToken;

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = window.setInterval(() => {
      setResendCooldown((seconds) => (seconds <= 1 ? 0 : seconds - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [resendCooldown]);

  useEffect(() => {
    if (error) errorRef.current?.focus();
  }, [error]);

  useEffect(() => {
    if (previousModeRef.current === mode) return;
    previousModeRef.current = mode;
    headingRef.current?.focus();
  }, [mode]);

  useEffect(
    () => () => {
      submitAttemptRef.current += 1;
      resendAttemptRef.current += 1;
      submitLockRef.current = false;
      resendLockRef.current = false;
    },
    [],
  );

  function resetCaptcha() {
    setCaptchaToken(null);
    setCaptchaNonce((nonce) => nonce + 1);
  }

  function clearPanels() {
    setConfirmSentTo(null);
    setResetSentTo(null);
    setResetComplete(false);
    setResendStatus('idle');
    setResendError(null);
    setResendCooldown(0);
  }

  function switchMode(nextMode: Mode) {
    submitAttemptRef.current += 1;
    resendAttemptRef.current += 1;
    submitLockRef.current = false;
    resendLockRef.current = false;
    setLoading(false);
    setMode(nextMode);
    setError(null);
    clearPanels();
    resetCaptcha();
  }

  function isCurrentSubmit(attempt: number) {
    return submitAttemptRef.current === attempt;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (submitLockRef.current) return;

    if (mode === 'reset' && password !== passwordConfirmation) {
      setError('The new passwords do not match.');
      return;
    }

    submitLockRef.current = true;
    const attempt = ++submitAttemptRef.current;
    setLoading(true);
    setError(null);

    let operation: AuthOperation =
      mode === 'signup'
        ? 'sign-up'
        : mode === 'forgot'
          ? 'request-reset'
          : mode === 'reset'
            ? 'update-password'
            : 'sign-in';

    try {
      if (mode === 'signin') {
        const { error: signInError } =
          await supabase.auth.signInWithPassword({
            email,
            password,
            options: { captchaToken: captchaToken ?? undefined },
          });
        if (!isCurrentSubmit(attempt)) return;
        if (signInError) throw signInError;
        navigate(next ?? '/account', { replace: true });
        return;
      }

      if (mode === 'signup') {
        const { data, error: signUpError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: buildAuthCallbackUrl(window.location.origin, {
              flow: 'confirmation',
              plan,
              next,
            }),
            captchaToken: captchaToken ?? undefined,
          },
        });
        if (!isCurrentSubmit(attempt)) return;
        if (signUpError) throw signUpError;

        if (!data.session || !data.user) {
          setConfirmSentTo({ email, plan });
          resetCaptcha();
          return;
        }

        if (plan === 'pro') {
          operation = 'checkout';
          await redirectToCheckout(data.user.id, data.session.access_token);
          return;
        }
        navigate(next ?? '/account', { replace: true });
        return;
      }

      if (mode === 'forgot') {
        const { error: resetError } =
          await supabase.auth.resetPasswordForEmail(email, {
            redirectTo: buildAuthCallbackUrl(window.location.origin, {
              flow: 'recovery',
              next,
            }),
            captchaToken: captchaToken ?? undefined,
          });
        if (!isCurrentSubmit(attempt)) return;
        if (resetError) throw resetError;
        // The same confirmation is shown regardless of whether the address
        // exists, so this page cannot be used for account enumeration.
        setResetSentTo(email);
        resetCaptcha();
        return;
      }

      const { error: updateError } = await supabase.auth.updateUser({
        password,
      });
      if (!isCurrentSubmit(attempt)) return;
      if (updateError) throw updateError;
      setPassword('');
      setPasswordConfirmation('');
      setResetComplete(true);
    } catch (caught) {
      if (!isCurrentSubmit(attempt)) return;
      setError(authFailureMessage(operation, caught));
      if (mode !== 'reset') resetCaptcha();
    } finally {
      if (isCurrentSubmit(attempt)) {
        submitLockRef.current = false;
        setLoading(false);
      }
    }
  }

  async function handleResend() {
    if (
      !confirmSentTo
      || resendLockRef.current
      || resendCooldown > 0
      || captchaMissing
    ) {
      return;
    }

    resendLockRef.current = true;
    const attempt = ++resendAttemptRef.current;
    setResendStatus('sending');
    setResendError(null);
    try {
      const { error: resendFailure } = await supabase.auth.resend({
        type: 'signup',
        email: confirmSentTo.email,
        options: {
          emailRedirectTo: buildAuthCallbackUrl(window.location.origin, {
            flow: 'confirmation',
            plan: confirmSentTo.plan,
            next,
          }),
          captchaToken: captchaToken ?? undefined,
        },
      });
      if (resendAttemptRef.current !== attempt) return;
      if (resendFailure) throw resendFailure;
      setResendStatus('sent');
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      resetCaptcha();
    } catch (caught) {
      if (resendAttemptRef.current !== attempt) return;
      setResendStatus('error');
      setResendError(authFailureMessage('resend-confirmation', caught));
      resetCaptcha();
    } finally {
      if (resendAttemptRef.current === attempt) resendLockRef.current = false;
    }
  }

  if (confirmSentTo) {
    return (
      <AuthShell heading="Check your email">
        <div
          className="px-4 py-4 bg-surface-1 border border-border rounded-md font-ui text-sm text-text-secondary leading-relaxed"
          role="status"
        >
          <p className="m-0 break-words [overflow-wrap:anywhere]">
            We sent a confirmation link to{' '}
            <strong className="text-text-primary">{confirmSentTo.email}</strong>.
            Click it to activate your account
            {confirmSentTo.plan === 'pro'
              ? ` — then we’ll take you to secure Stripe checkout to start your ${PRO_TRIAL_SUMMARY} (card required, cancel anytime before it ends).`
              : '.'}
          </p>
          <p className="mt-3 mb-0 text-text-tertiary">
            Didn’t get it? Check your spam folder, or resend it below.
          </p>
        </div>

        {turnstileSiteKey && (
          <div className="mt-4">
            <Turnstile
              key={captchaNonce}
              siteKey={turnstileSiteKey}
              onToken={(token) => {
                setCaptchaToken(token);
                setResendError(null);
              }}
              onError={(message) => {
                setCaptchaToken(null);
                setResendError(message);
              }}
              onExpire={() => setCaptchaToken(null)}
            />
          </div>
        )}

        <button
          type="button"
          onClick={handleResend}
          disabled={
            resendStatus === 'sending'
            || resendCooldown > 0
            || captchaMissing
          }
          className={cn(btnBase, btnPrimary, 'w-full mt-4 cursor-pointer')}
        >
          {resendStatus === 'sending'
            ? 'Resending…'
            : resendCooldown > 0
              ? `Resend confirmation email (${resendCooldown}s)`
              : 'Resend confirmation email'}
        </button>

        {resendStatus === 'sent' && (
          <p
            className="mt-2 text-center font-ui text-sm text-text-secondary"
            role="status"
          >
            Sent — check your inbox.
          </p>
        )}
        {resendStatus === 'error' && resendError && (
          <p
            className="mt-2 text-center font-ui text-sm text-error-text break-words [overflow-wrap:anywhere]"
            role="alert"
          >
            {resendError}
          </p>
        )}

        <button
          type="button"
          onClick={() => switchMode('signin')}
          className={cn(
            btnBase,
            'w-full mt-2 bg-transparent text-text-secondary hover:bg-surface-1 cursor-pointer',
          )}
        >
          Back to sign in
        </button>
      </AuthShell>
    );
  }

  if (resetSentTo) {
    return (
      <AuthShell heading="Check your email">
        <div
          className="px-4 py-4 bg-surface-1 border border-border rounded-md font-ui text-sm text-text-secondary leading-relaxed"
          role="status"
        >
          <p className="m-0 break-words [overflow-wrap:anywhere]">
            If an account matches{' '}
            <strong className="text-text-primary">{resetSentTo}</strong>, a
            password-reset link is on its way.
          </p>
          <p className="mt-3 mb-0 text-text-tertiary">
            The link can be used once. If it expires, request another.
          </p>
        </div>
        <button
          type="button"
          onClick={() => switchMode('forgot')}
          className={cn(btnBase, btnPrimary, 'w-full mt-4 cursor-pointer')}
        >
          Send another reset link
        </button>
        <button
          type="button"
          onClick={() => switchMode('signin')}
          className={cn(
            btnBase,
            'w-full mt-2 bg-transparent text-text-secondary hover:bg-surface-1 cursor-pointer',
          )}
        >
          Back to sign in
        </button>
      </AuthShell>
    );
  }

  if (resetComplete) {
    return (
      <AuthShell heading="Password updated">
        <p
          className="rounded-md border border-success bg-success-bg px-4 py-3 font-ui text-sm text-success-text"
          role="status"
        >
          Your password was updated successfully.
        </p>
        <button
          type="button"
          onClick={() => navigate(next ?? '/account', { replace: true })}
          className={cn(btnBase, btnPrimary, 'w-full mt-4 cursor-pointer')}
        >
          Continue to account
        </button>
      </AuthShell>
    );
  }

  const heading =
    mode === 'signup'
      ? 'Create your account'
      : mode === 'forgot'
        ? 'Reset your password'
        : mode === 'reset'
          ? 'Choose a new password'
          : 'Sign in to your account';
  const submitLabel = loading
    ? mode === 'signup'
      ? 'Creating account…'
      : mode === 'forgot'
        ? 'Sending reset link…'
        : mode === 'reset'
          ? 'Updating password…'
          : 'Signing in…'
    : mode === 'signup'
      ? 'Create account'
      : mode === 'forgot'
        ? 'Send reset link'
        : mode === 'reset'
          ? 'Update password'
          : 'Sign in';

  return (
    <main className="w-full max-w-[400px] mx-auto my-16 px-4 max-mobile:my-8 min-w-0">
      <BrandHeader />
      <h1
        ref={headingRef}
        tabIndex={-1}
        className="text-center text-text-tertiary mb-6 font-ui text-sm font-normal outline-none"
      >
        {heading}
      </h1>

      {(mode === 'signin' || mode === 'signup') && (
        <div
          className="flex gap-1 mb-6 p-1 bg-surface-1 border border-border rounded-md"
          aria-label="Authentication method"
        >
          {(['signin', 'signup'] as const).map((candidate) => (
            <button
              key={candidate}
              type="button"
              onClick={() => switchMode(candidate)}
              aria-pressed={mode === candidate}
              aria-label={
                candidate === 'signin'
                  ? 'Show sign in form'
                  : 'Show create account form'
              }
              className={cn(
                'flex-1 py-1.5 rounded-sm font-ui text-sm transition-colors',
                mode === candidate
                  ? 'bg-surface-white text-text-primary shadow-sm'
                  : 'text-text-tertiary hover:text-text-secondary',
              )}
            >
              {candidate === 'signin' ? 'Sign in' : 'Create account'}
            </button>
          ))}
        </div>
      )}

      {error && (
        <div
          ref={errorRef}
          tabIndex={-1}
          className="text-error-text mb-4 font-ui text-sm break-words [overflow-wrap:anywhere] outline-none"
          role="alert"
        >
          {error}
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-4 min-w-0"
        aria-busy={loading}
      >
        {mode !== 'reset' && (
          <div className={field}>
            <label htmlFor="email" className={fieldLabel}>
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              autoComplete="email"
              className={fieldInput}
            />
          </div>
        )}

        {(mode === 'signin' || mode === 'signup' || mode === 'reset') && (
          <div className={field}>
            <label htmlFor="password" className={fieldLabel}>
              {mode === 'reset' ? 'New password' : 'Password'}
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
              minLength={6}
              autoComplete={
                mode === 'signin' ? 'current-password' : 'new-password'
              }
              className={fieldInput}
            />
          </div>
        )}

        {mode === 'reset' && (
          <div className={field}>
            <label htmlFor="password-confirmation" className={fieldLabel}>
              Confirm new password
            </label>
            <input
              id="password-confirmation"
              type="password"
              value={passwordConfirmation}
              onChange={(event) =>
                setPasswordConfirmation(event.target.value)
              }
              required
              minLength={6}
              autoComplete="new-password"
              className={fieldInput}
            />
          </div>
        )}

        {mode === 'signup' && (
          <div className={field}>
            <span className={fieldLabel}>Plan</span>
            <div className="grid grid-cols-2 gap-2 max-mobile:grid-cols-1">
              <PlanOption
                selected={plan === 'free'}
                onSelect={() => setPlan('free')}
                title="Free"
                price="$0"
                blurb="Public-record primary law"
                disabled={loading}
              />
              <PlanOption
                selected={plan === 'pro'}
                onSelect={() => setPlan('pro')}
                title="Pro"
                price={PRO_PRICE_LABEL}
                blurb="Agency guidance + practitioner verticals (patent, tax, labor, cap-markets, immigration, energy) + deep case analysis"
                disabled={loading}
              />
            </div>
            {plan === 'pro' && (
              <p className="font-ui text-xs text-text-tertiary mt-1 break-words [overflow-wrap:anywhere]">
                Start your {PRO_TRIAL_SUMMARY} — card required, cancel anytime
                before it ends. After you confirm your email, we’ll take you to
                secure Stripe checkout.
              </p>
            )}
          </div>
        )}

        {turnstileSiteKey && mode !== 'reset' && (
          <Turnstile
            key={captchaNonce}
            siteKey={turnstileSiteKey}
            onToken={(token) => {
              setCaptchaToken(token);
              setError(null);
            }}
            onError={(message) => {
              setCaptchaToken(null);
              setError(message);
            }}
            onExpire={() => setCaptchaToken(null)}
          />
        )}

        <button
          type="submit"
          disabled={loading || captchaMissing}
          className={cn(btnBase, btnPrimary, 'w-full mt-2 cursor-pointer')}
        >
          {submitLabel}
        </button>
      </form>

      {mode === 'signin' && (
        <button
          type="button"
          onClick={() => switchMode('forgot')}
          className="mt-4 w-full font-ui text-sm text-navy-700 underline underline-offset-2"
        >
          Forgot password?
        </button>
      )}
      {(mode === 'forgot' || mode === 'reset') && (
        <button
          type="button"
          onClick={() => switchMode('signin')}
          className="mt-4 w-full font-ui text-sm text-navy-700 underline underline-offset-2"
        >
          Back to sign in
        </button>
      )}
    </main>
  );
}

function BrandHeader() {
  return (
    <div className="flex flex-col items-center gap-1.5 mb-7 min-w-0">
      <Wordmark size="lg" />
      <p className="text-text-tertiary font-ui text-xs m-0 text-center break-words [overflow-wrap:anywhere]">
        Primary-source legal research inside Claude
      </p>
    </div>
  );
}

function AuthShell({
  heading,
  children,
}: {
  heading: string;
  children: React.ReactNode;
}) {
  const shellHeadingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    shellHeadingRef.current?.focus();
  }, []);

  return (
    <main className="w-full max-w-[400px] mx-auto my-16 px-4 max-mobile:my-8 min-w-0">
      <BrandHeader />
      <h1
        ref={shellHeadingRef}
        tabIndex={-1}
        className="text-center text-text-tertiary mb-6 font-ui text-sm font-normal outline-none"
      >
        {heading}
      </h1>
      {children}
    </main>
  );
}

function PlanOption({
  selected,
  onSelect,
  title,
  price,
  blurb,
  disabled,
}: {
  selected: boolean;
  onSelect: () => void;
  title: string;
  price: string;
  blurb: string;
  disabled: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      disabled={disabled}
      className={cn(
        'flex min-w-0 flex-col items-start gap-0.5 rounded-md border px-3 py-2.5 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60',
        selected
          ? 'border-navy-500 bg-navy-100'
          : 'border-border bg-surface-white hover:border-border-emphasis',
      )}
    >
      <span className="font-ui text-sm font-medium text-text-primary break-words [overflow-wrap:anywhere]">
        {title}{' '}
        <span className="text-text-tertiary font-normal">· {price}</span>
      </span>
      <span className="font-ui text-xs text-text-tertiary leading-snug break-words [overflow-wrap:anywhere]">
        {blurb}
      </span>
    </button>
  );
}
